import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, Download, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { useLang } from "../i18n";
import { DynRows, Field } from "../components/fields";
import { AttachmentInput, PublicLinkButton } from "../components/FormLink";
import SignaturePad from "../components/SignaturePad";
import FormRecordsList from "../components/FormRecordsList";
import { Plus } from "lucide-react";
import DateInput from "../components/DateInput";

export default function FormCertificacion({ publicToken = null }) {
  const { t } = useLang();
  const initial = {
    date: new Date().toISOString().slice(0, 10),
    signer: { name: "", id_number: "", capacity: t("c.cap_legal_rep") },
    company_name: "",
    ubos: [],
    client_name: "", signer_position: "", email: "", signature: null,
  };
  const locationEdit = useLocation().state?.edit || null;
  const [editDoc, setEditDoc] = useState(locationEdit);
  const [showForm, setShowForm] = useState(!!locationEdit);
  const [d, setD] = useState(() => (locationEdit ? { ...initial, ...locationEdit.data } : initial));
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [atts, setAtts] = useState([]);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setD({ ...d, [k]: e?.target ? e.target.value : e });
  const setS = (k) => (e) => setD({ ...d, signer: { ...d.signer, [k]: e.target.value } });

  const scrollToForm = () => setTimeout(() => document.querySelector('[data-testid="bf-form-wrap"]')?.scrollIntoView({ behavior: "smooth" }), 80);

  const newForm = () => {
    setEditDoc(null);
    setD(initial);
    setSaved(null);
    setShowForm(true);
    scrollToForm();
  };

  const startEdit = (doc) => {
    setEditDoc(doc);
    setD({ ...initial, ...doc.data });
    setSaved(null);
    setShowForm(true);
    scrollToForm();
  };
  const total = d.ubos.reduce((acc, u) => acc + (parseFloat(u.ownership_pct) || 0), 0);

  const submit = async () => {
    if (!d.signature) {
      toast.error(t("forms.sign_required"));
      return;
    }
    setBusy(true);
    try {
      if (publicToken) {
        const { data } = await api.post(`/public/form/${publicToken}`, { data: d, attachments: atts });
        setDone(data.form_number);
      } else if (editDoc) {
        await api.patch(`/forms/${editDoc.id}`, { data: d });
        setSaved({ id: editDoc.id, form_number: editDoc.form_number });
        toast.success(t("forms.updated"));
      } else {
        const { data } = await api.post("/forms", { form_type: "certificacion_beneficiario", data: d, attachments: atts });
        setSaved(data);
        toast.success(`${t("forms.save_success")} — ${data.form_number}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="ec-card p-10 text-center space-y-3 max-w-xl mx-auto" data-testid="form-public-done">
        <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
        <h2 className="font-heading text-xl font-extrabold text-slate-900">{t("forms.public_done")}</h2>
        <p className="text-sm text-slate-600">{t("forms.public_done_no")} <span className="font-mono font-bold">{done}</span></p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="form-certificacion-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("c.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("c.subtitle")}</p>
        </div>
        {!publicToken && (
          <div className="flex items-center gap-2">
            <button onClick={newForm} data-testid="bf-create-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] transition-colors">
              <Plus className="w-4 h-4" /> {t("frl.new")}
            </button>
            <PublicLinkButton formType="certificacion_beneficiario" testid="bf-public-link" />
          </div>
        )}
      </div>

      {!publicToken && <FormRecordsList formType="certificacion_beneficiario" onEdit={startEdit} refresh={saved} testid="bf" />}

      {saved && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3" data-testid="form-saved-banner">
          <span className="text-sm text-emerald-800 font-semibold">{t("forms.doc_no")} <span className="font-mono">{saved.form_number}</span></span>
          <button onClick={() => downloadFile(`/forms/${saved.id}/pdf`, `${saved.form_number}.pdf`)} data-testid="download-form-pdf" className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold hover:bg-[#033A8C]">
            <Download className="w-3.5 h-3.5" /> {t("common.download_pdf")}
          </button>
        </div>
      )}

      {(publicToken || showForm) && (
      <div className="ec-card p-6 space-y-6" data-testid="bf-form-wrap">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label={t("c.date")}><DateInput className="ec-input" value={d.date} onChange={set("date")} data-testid="bf-date" /></Field>
          <Field label={t("c.signer_name")}><input className="ec-input" value={d.signer.name} onChange={setS("name")} data-testid="bf-signer-name" /></Field>
          <Field label={t("c.signer_id")}><input className="ec-input" value={d.signer.id_number} onChange={setS("id_number")} data-testid="bf-signer-id" /></Field>
          <Field label={t("c.capacity")}>
            <select className="ec-input" value={d.signer.capacity} onChange={setS("capacity")} data-testid="bf-capacity">
              {[t("c.cap_president"), t("c.cap_secretary"), t("c.cap_legal_rep"), t("c.cap_attorney")].map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label={t("c.company_name")} className="lg:col-span-2"><input className="ec-input" value={d.company_name} onChange={set("company_name")} data-testid="bf-company-name" /></Field>
        </div>

        <div>
          <h3 className="font-heading font-bold text-slate-900 mb-3">{t("c.ubos")}</h3>
          <DynRows testid="bf-ubos" rows={d.ubos} onChange={(v) => setD({ ...d, ubos: v })} columns={[
            { key: "name", label: t("c.col_name") }, { key: "id_number", label: t("c.col_id") },
            { key: "nationality", label: t("c.col_nat") }, { key: "role", label: t("c.col_role") },
            { key: "birth_or_address", label: t("c.col_birth_addr") },
            { key: "ownership_pct", label: t("c.col_pct"), type: "number" },
            { key: "public_figure", label: t("c.col_public") },
          ]} />
          <div className="mt-2 flex items-center gap-3" data-testid="bf-pct-total">
            <span className="text-sm font-semibold text-slate-700">{t("c.pct_total")}: <span className="font-mono">{total.toFixed(1)}%</span></span>
            {d.ubos.length > 0 && Math.abs(total - 100) > 0.01 && (
              <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded">{t("c.pct_warn")}</span>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5 pt-4 border-t border-slate-100">
          <div className="space-y-4">
            <Field label={t("c.rep_name")}><input className="ec-input" value={d.client_name} onChange={set("client_name")} data-testid="bf-rep-name" /></Field>
            <Field label={t("auth.email")}><input className="ec-input" type="email" value={d.email} onChange={set("email")} data-testid="bf-email" /></Field>
            <Field label={t("c.rep_position")}><input className="ec-input" value={d.signer_position} onChange={set("signer_position")} data-testid="bf-rep-position" /></Field>
            <AttachmentInput value={atts} onChange={setAtts} testid="bf-attachments" />
          </div>
          <Field label={t("forms.signature")}><SignaturePad onChange={(v) => setD((p) => ({ ...p, signature: v }))} /></Field>
        </div>

        <div className="flex justify-end">
          <button onClick={submit} disabled={busy} data-testid="bf-submit" className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {busy ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
      )}
    </div>
  );
}
